/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {CheckBox} from '../components/checkbox/CheckBox';
import PulseLoader from '../components/loading/PulseLoader';
import Tooltip from '../components/tooltip/Tooltip';
import {MaterialAddIcon} from '../icons/current/MaterialAddIcon';
import {MaterialRemoveIcon} from '../icons/current/MaterialRemoveIcon';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';
import FilterTreeNodeIcon from './FilterTreeNodeIcon';
import {FilterTreeNodeBase} from './types';
import {
  collectFilterTreeNodeKeys,
  getFilterTreeNodeKey,
  getFilterTreeNodeSelectionState,
  isFilterTreeGroupFullyShown
} from './utils';

// the indent each tree level adds, for groups and assets alike ...
const FILTER_TREE_INDENT = 20;

interface FilterTreeChecklistNodeRowProps {
  batchSize: number;
  classes: Record<string, string>;
  disabled: boolean;
  expandedIds: Set<string>;
  level: number;
  // the group the tree's loader shows under, while it is busy ...
  loaderNodeKey?: string | null;
  loadingLabel?: string;
  node: FilterTreeNodeBase;
  onRevealMore: (node: FilterTreeNodeBase) => void;
  onToggle: (node: FilterTreeNodeBase) => void;
  onToggleExpand: (node: FilterTreeNodeBase) => void;
  paginationDisabled: boolean;
  selected: Set<string>;
  showAllLabel: string;
  showLessLabel: string;
  visibleCounts: Record<string, number>;
}

/**
 * Renders one recursive row for a tree checklist filter.
 *
 * ```typescript
 * import FilterTreeChecklistNodeRow from '@ion/dls/src/filters/FilterTreeChecklistNodeRow';
 *
 * ...
 *
 * <FilterTreeChecklistNodeRow node={node}
 *   batchSize={10}
 *   classes={classes}
 *   disabled={disabled}
 *   expandedIds={expandedIds}
 *   level={0}
 *   onRevealMore={revealMore}
 *   onToggle={toggleNode}
 *   onToggleExpand={toggleExpand}
 *   paginationDisabled={false}
 *   selected={selected}
 *   showAllLabel='Show all'
 *   showLessLabel='Show less'
 *   visibleCounts={visibleCounts} />
 * ```
 */
export const FilterTreeChecklistNodeRow = ({
  batchSize,
  classes,
  disabled,
  expandedIds,
  level,
  loaderNodeKey = null,
  loadingLabel,
  node,
  onRevealMore,
  onToggle,
  onToggleExpand,
  paginationDisabled,
  selected,
  showAllLabel,
  showLessLabel,
  visibleCounts
}: FilterTreeChecklistNodeRowProps) => {
  const nodeKey                  = getFilterTreeNodeKey(node);
  const children                 = node.children ?? [];
  const hasChildren              = !!children.length || node.hasMore;
  // a group keeps its expand arrow even when it turns out to be empty
  // (disabled then), so every group lines up the same way ...
  const showExpander             = hasChildren || node.type === 'group';
  // an empty group's disabled arrow says why on hover ...
  const emptyGroupTooltip        = hasChildren ? undefined : (
    <span>
      {translate('filters:options.emptyGroup', {name: node.label})}
    </span>
  );
  const expanded                 = expandedIds.has(nodeKey);
  const {checked, indeterminate} = getFilterTreeNodeSelectionState(node, selected);
  // groups and assets share one indent step, so siblings line up ...
  const indent                   = Math.max(level * FILTER_TREE_INDENT, 0);
  // a group shows its first batch until 'show all' (or revealing a search
  // result) raises its count ...
  const visibleCount             = visibleCounts[nodeKey] ?? batchSize;
  const visibleChildren          = paginationDisabled ? children : children.slice(0, visibleCount);
  const canToggleChildren        = children.length > batchSize || node.hasMore;
  // 'show less' only once every child shows ...
  const childrenExpanded         = isFilterTreeGroupFullyShown(node, visibleCounts[nodeKey], batchSize);

  const handleToggleExpand = useCallback(() => {
    onToggleExpand(node);
  }, [node, onToggleExpand]);

  const handleToggle = useCallback(() => {
    onToggle(node);
  }, [node, onToggle]);

  const handleRevealMore = useCallback(() => {
    onRevealMore(node);
  }, [node, onRevealMore]);

  return (
    <>
      <div className={classes.row}
        style={{paddingLeft: indent}}
        role='treeitem'
        aria-expanded={hasChildren ? expanded : undefined}
        aria-level={level + 1}
        data-node-key={nodeKey}>
        <Render>
          <Render.If condition={showExpander}>
            {/* the tooltip hovers the wrapper - a disabled button gets no mouse events ... */}
            <Tooltip arrow
              title={emptyGroupTooltip}
              popperClassName={classes.expanderTooltipPopper}>
              <span className={classes.expanderWrapper}>
                <button type='button'
                  className={classes.expander}
                  disabled={disabled || !hasChildren}
                  aria-label={node.label}
                  aria-expanded={expanded && hasChildren}
                  onClick={handleToggleExpand}>
                  <span className={classnames(classes.arrow, {[classes.arrowExpanded]: expanded && hasChildren})} />
                </button>
              </span>
            </Tooltip>
          </Render.If>
          <Render.ElseIf condition={node.type === 'asset'}>
            <span className={classes.assetIcon}>
              <FilterTreeNodeIcon isAsset />
            </span>
          </Render.ElseIf>
          <Render.Else>
            <span className={classes.expanderSpacer} />
          </Render.Else>
        </Render>
        <span className={classes.checkbox}>
          <CheckBox checked={checked}
            disabled={disabled || node.disabled}
            indeterminate={indeterminate}
            inputProps={{'aria-label': node.label}}
            onChange={handleToggle} />
        </span>
        <span className={classnames(classes.label, {[classes.groupLabel]: node.type === 'group'})}>
          {node.label}
        </span>
      </div>
      <Render.If condition={loaderNodeKey === nodeKey}>
        <div className={classes.loaderRow}>
          <PulseLoader message={loadingLabel} />
        </div>
      </Render.If>
      <Render.If condition={expanded}>
        <div className={classes.children} role='group'>
          {visibleChildren.map(child => (
            <MemoizedFilterTreeChecklistNodeRow key={getFilterTreeNodeKey(child)}
              batchSize={batchSize}
              classes={classes}
              disabled={disabled}
              expandedIds={expandedIds}
              level={level + 1}
              loaderNodeKey={loaderNodeKey}
              loadingLabel={loadingLabel}
              node={child}
              onRevealMore={onRevealMore}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
              paginationDisabled={paginationDisabled}
              selected={selected}
              showAllLabel={showAllLabel}
              showLessLabel={showLessLabel}
              visibleCounts={visibleCounts} />
          ))}
          {/* hidden, not disabled, while the tree is filtered - every match shows then ... */}
          <Render.If condition={canToggleChildren && !node.loading && !paginationDisabled}>
            <div className={classes.showAllRow}>
              <button type='button'
                className={classes.showAllButton}
                aria-expanded={childrenExpanded}
                onClick={handleRevealMore}>
                <Render>
                  <Render.If condition={childrenExpanded}>
                    <MaterialRemoveIcon color={dlsColor('darkestBlue')}
                      size={IconSize.small} />
                  </Render.If>
                  <Render.Else>
                    <MaterialAddIcon color={dlsColor('darkestBlue')}
                      size={IconSize.small} />
                  </Render.Else>
                </Render>
                {childrenExpanded ? showLessLabel : showAllLabel}
              </button>
            </div>
          </Render.If>
        </div>
      </Render.If>
    </>
  );
};

const areNodeRowPropsEqual = (
  previous: FilterTreeChecklistNodeRowProps,
  next: FilterTreeChecklistNodeRowProps
) => {
  const stablePropsEqual = previous.batchSize === next.batchSize &&
    previous.classes === next.classes &&
    previous.disabled === next.disabled &&
    previous.level === next.level &&
    previous.loadingLabel === next.loadingLabel &&
    previous.node === next.node &&
    previous.onRevealMore === next.onRevealMore &&
    previous.onToggle === next.onToggle &&
    previous.onToggleExpand === next.onToggleExpand &&
    previous.paginationDisabled === next.paginationDisabled &&
    previous.showAllLabel === next.showAllLabel &&
    previous.showLessLabel === next.showLessLabel;
  if (!stablePropsEqual) {
    return false;
  }
  // only what this row's own subtree shows matters - a 'show all' in one
  // group (a new visibleCounts) or a tick elsewhere leaves every other row
  // as it is, which keeps a tree of thousands of groups fast ...
  return collectFilterTreeNodeKeys(next.node).every(key => (
    previous.selected.has(key) === next.selected.has(key) &&
    previous.expandedIds.has(key) === next.expandedIds.has(key) &&
    previous.visibleCounts[key] === next.visibleCounts[key] &&
    (previous.loaderNodeKey === key) === (next.loaderNodeKey === key)
  ));
};

const MemoizedFilterTreeChecklistNodeRow = memo(
  FilterTreeChecklistNodeRow,
  areNodeRowPropsEqual
);

export default MemoizedFilterTreeChecklistNodeRow;
